import * as React from "react";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import { Typography, IconButton } from "@material-ui/core";

// NPM
import clsx from "clsx";
import moment from "moment";
import { animated } from "react-spring";

// Classes
import Utility from "../../domain/classes/Utility";
import DateTime from "../../domain/classes/DateTime";

// Icons
import ArrowBackIosRoundedIcon from "@material-ui/icons/ArrowBackIosRounded";
import ArrowForwardIosRoundedIcon from "@material-ui/icons/ArrowForwardIosRounded";
import { useCalendarManager } from "../../contexts/CalendarManager.Context";
import Calendar from "../../domain/classes/Calendar";

export const styles = (theme: Theme) =>
	createStyles({
		root: { marginBottom: theme.spacing(2), display: "flex" },
		header: { flexGrow: 1, display: "flex", alignItems: "center" },
		headerText: { display: "block", flexGrow: 1 },
		iconButton: { border: `1px solid ${theme.palette.primary.main}` },
		buttonContainer: { display: "flex", alignItems: "center" },
	});

export interface DateHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
	date: string;
}

const DateHeader = React.forwardRef<
	HTMLDivElement,
	DateHeaderProps & WithStyles<typeof styles>
>(({ classes, className, date, ...divProps }, ref) => {
	const {
		dateRange,
		rangeSize,
		handleDateRangeChange,
	} = useCalendarManager();

	const index = dateRange.indexOf(date);

	const handleForwardButtonClick = () => {
		const lastDateOfRange = dateRange[rangeSize - 1];
		const theDate = moment(lastDateOfRange).add(1, "day");

		const newRange = Calendar.generateDateRangeFrom(
			theDate.format(DateTime.DEFAULT_DATE_FORMAT),
			rangeSize
		);
		handleDateRangeChange(newRange);
	};

	const handleBackwardButtonClick = () => {
		const firstDateOfRange = dateRange[0];
		const theDate = moment(firstDateOfRange);
		let firstDateOfNewRange = theDate.subtract(rangeSize, "days");

		if (firstDateOfNewRange.isBefore(moment())) {
			firstDateOfNewRange = moment();
		}
		const newRange = Calendar.generateDateRangeFrom(
			firstDateOfNewRange.format(DateTime.DEFAULT_DATE_FORMAT),
			rangeSize
		);
		handleDateRangeChange(newRange);
	};

	const renderForwardButton = (): boolean => {
		return (
			(index === 2 && rangeSize === 3) ||
			(index === 1 && rangeSize === 2) ||
			(index === 0 && rangeSize === 1)
		);
	};

	return (
		<div {...divProps} className={clsx(classes.root, className)} ref={ref}>
			{index === 0 && (
				<div className={classes.buttonContainer}>
					<IconButton
						className={classes.iconButton}
						size="small"
						onClick={handleBackwardButtonClick}
						disabled={
							moment().format(DateTime.DEFAULT_DATE_FORMAT) ===
							date
						}
					>
						<ArrowBackIosRoundedIcon
							fontSize="small"
							color="primary"
						/>
					</IconButton>
				</div>
			)}
			<div className={classes.header}>
				<Typography
					variant="h5"
					color="primary"
					align="center"
					className={classes.headerText}
				>
					<strong>
						{moment(date).format(
							DateTime.FULL_DATE_DISPLAY_FORMAT
						) === moment().format(DateTime.FULL_DATE_DISPLAY_FORMAT)
							? "Today"
							: moment(date).format(
									DateTime.FULL_DATE_DISPLAY_FORMAT
							  )}
					</strong>
				</Typography>
			</div>
			{renderForwardButton() && (
				<div className={classes.buttonContainer}>
					<IconButton
						className={classes.iconButton}
						size="small"
						onClick={handleForwardButtonClick}
					>
						<ArrowForwardIosRoundedIcon
							fontSize="small"
							color="primary"
						/>
					</IconButton>
				</div>
			)}
		</div>
	);
});

const StyledDateHeader = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("StyledDateHeader"),
})(DateHeader);

export default StyledDateHeader;

export const AnimatedDateHeader = animated(StyledDateHeader);
